"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";

import { trackSiteGlueEvent } from "~/lib/siteglue/cta-config";
import {
  AUDIT_CHOOSER_DEFAULT,
  AUDIT_CHOOSER_LABEL,
  AUDIT_CHOOSER_OPTIONS,
  HOMEPAGE_URL_LABEL,
  HOMEPAGE_URL_PLACEHOLDER,
  RUN_MY_FREE_AUDIT_CTA,
  type AuditChooserId,
} from "~/lib/siteglue/landing-redesign/copy";
import { trackLandingEvent } from "~/lib/siteglue/landing-redesign/events";
import {
  normalizeBusinessWebsite,
  persistLandingUrl,
  URL_HELPER_INVALID,
} from "~/lib/siteglue/landing-redesign/url";

function hrefForAudit(id: AuditChooserId, website?: string): string {
  const href =
    AUDIT_CHOOSER_OPTIONS.find((option) => option.id === id)?.href ?? AUDIT_CHOOSER_OPTIONS[0].href;
  if (!website) return href;
  return `${href}?url=${encodeURIComponent(website)}`;
}

export function AuditChooserForm({
  location = "homepage_hero",
}: {
  location?: "homepage_hero" | "homepage_final" | "how_it_works";
} = {}) {
  const router = useRouter();
  const [selected, setSelected] = useState<AuditChooserId>(AUDIT_CHOOSER_DEFAULT);
  const [url, setUrl] = useState("");
  const [error, setError] = useState<string | null>(null);
  const showUrl = location !== "homepage_hero" && location !== "homepage_final";

  return (
    <form
      className="w-full max-w-2xl space-y-3"
      onSubmit={(event) => {
        event.preventDefault();
        const trimmed = url.trim();
        let website: string | undefined;
        if (showUrl && trimmed) {
          const normalized = normalizeBusinessWebsite(trimmed);
          if (!normalized.ok) {
            setError(URL_HELPER_INVALID);
            return;
          }
          website = normalized.href;
          persistLandingUrl(website);
        }
        setError(null);
        const href = hrefForAudit(selected, website);
        trackLandingEvent("url_submit", { url_source: location, audit: selected });
        trackSiteGlueEvent("homepage_primary_cta_click", { destination: href, audit: selected });
        router.push(href);
      }}
    >
      {showUrl ? (
        <>
          <label className="block text-sm font-semibold text-slate-800" htmlFor={`${location}-url`}>
            {HOMEPAGE_URL_LABEL}
          </label>
          <input
            id={`${location}-url`}
            type="text"
            inputMode="url"
            autoComplete="url"
            placeholder={HOMEPAGE_URL_PLACEHOLDER}
            value={url}
            onChange={(event) => {
              setUrl(event.target.value);
              if (error) setError(null);
            }}
            className="h-12 w-full rounded-full border border-slate-300 bg-white px-4 text-sm text-slate-900"
          />
          {error ? <p className="text-sm text-red-600">{error}</p> : null}
        </>
      ) : null}
      <label className="block text-sm font-semibold text-slate-800" htmlFor={`${location}-audit`}>
        {AUDIT_CHOOSER_LABEL}
      </label>
      <div className="flex flex-col gap-3 sm:flex-row">
        <select
          id={`${location}-audit`}
          value={selected}
          onChange={(event) => setSelected(event.target.value as AuditChooserId)}
          className="h-12 min-w-0 flex-1 rounded-full border border-slate-300 bg-white px-4 text-sm font-medium text-slate-900"
        >
          {AUDIT_CHOOSER_OPTIONS.map((option) => (
            <option key={option.id} value={option.id}>
              {option.label}
            </option>
          ))}
        </select>
        <button
          type="submit"
          className="inline-flex h-12 shrink-0 items-center justify-center rounded-full bg-[#4f81bd] px-6 text-sm font-semibold text-white hover:opacity-95"
        >
          {RUN_MY_FREE_AUDIT_CTA}
        </button>
      </div>
    </form>
  );
}
